import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import BaseSelect from "../../Base/BaseSelect";
import showToast from "../../../utils/toast";
import useLocationsService, { Location } from "../../../services/useLocationsService";
import useServicesService from "../../../services/useServicesService";
import { Service } from "../../../models/ServiceModel";
import { ServiceStatus } from "../../../enums/ServiceEnum";
import RouteStopsEditor, { RouteStopDraft, stopsToDrafts } from "./RouteStopsEditor";
import "../../../pages/Services/styles/ServiceEditPage.css";

const FINAL_STATUSES = [
  ServiceStatus.COMPLETADO,
  ServiceStatus.ANULADO,
  ServiceStatus.CANCELADO,
];

const normalizeUiError = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(normalizeUiError).filter(Boolean).join(" ");
  if (value && typeof value === "object") {
    const first = Object.values(value as Record<string, unknown>)
      .map(normalizeUiError)
      .find(Boolean);
    return first || "Error del servidor";
  }
  return "Error del servidor";
};

type Props = {
  service: Service | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
};

const ServiceRouteEditModal: React.FC<Props> = ({
  service,
  isOpen,
  onClose,
  onSaved,
}) => {
  const { list: listLocations } = useLocationsService();
  const { updateRoute, getRoute, fetching } = useServicesService();

  const [locations, setLocations] = useState<Location[]>([]);
  const [routeOrigin, setRouteOrigin] = useState("");
  const [routeDestination, setRouteDestination] = useState("");
  const [routeComment, setRouteComment] = useState("");
  const [routeInvert, setRouteInvert] = useState(false);
  const [routeStops, setRouteStops] = useState<RouteStopDraft[]>([]);

  useEffect(() => {
    if (!isOpen || !service) return;

    setRouteOrigin(String(service.origin ?? ""));
    setRouteDestination(String(service.destination ?? ""));
    setRouteComment("");
    setRouteInvert(false);
    setRouteStops([]);

    if (service.id) {
      getRoute(service.id, {
        onSuccess: (resp) => setRouteStops(stopsToDrafts(resp?.data?.stops)),
        onError: () => showToast("No se pudieron cargar los puntos de la ruta", "error"),
      });
    }

    listLocations(
      { all: true },
      {
        onSuccess: (resp) => {
          const data = resp?.data;
          const items = Array.isArray(data) ? data : data?.locations ?? [];
          setLocations(Array.isArray(items) ? items : []);
        },
        onError: () => showToast("No se pudieron cargar las localizaciones", "error"),
      }
    );
  }, [isOpen, service, listLocations, getRoute]);

  if (!isOpen || !service) return null;

  if (FINAL_STATUSES.includes(service.status as ServiceStatus)) {
    return createPortal(
      <div className="sp-quick-passenger-overlay" onClick={onClose}>
        <div
          className="sp-quick-passenger-modal sp-route-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sp-quick-passenger-head">
            <h4>Modificar ruta</h4>
            <button type="button" onClick={onClose} aria-label="Cerrar">
              ×
            </button>
          </div>
          <div className="sp-quick-passenger-body">
            <p className="sp-route-invert-note">
              No se puede modificar la ruta de un servicio finalizado.
            </p>
          </div>
          <div className="sp-quick-passenger-actions">
            <button
              type="button"
              className="sp-quick-passenger-btn sp-quick-passenger-btn--ghost"
              onClick={onClose}
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  const locationOptions = locations
    .filter((l) => l.id)
    .map((l) => ({ value: String(l.id), label: l.name ?? "" }));

  const swapRouteEndpoints = () => {
    const nextOrigin = routeDestination;
    const nextDestination = routeOrigin;
    setRouteOrigin(nextOrigin);
    setRouteDestination(nextDestination);
    setRouteStops((prev) => [...prev].reverse());
    const originalOrigin = String(service.origin ?? "");
    const originalDestination = String(service.destination ?? "");
    setRouteInvert(
      nextOrigin === originalDestination && nextDestination === originalOrigin
    );
  };

  const saveRoute = () => {
    if (!routeComment.trim()) {
      showToast("Debes ingresar un comentario para modificar la ruta.", "error");
      return;
    }
    const payload: {
      origin?: number;
      destination?: number;
      comment: string;
      invert?: boolean;
      stops: number[];
    } = {
      comment: routeComment.trim(),
      invert: routeInvert,
      stops: routeStops.filter((s) => s.location).map((s) => Number(s.location)),
    };
    if (!routeInvert) {
      if (!routeOrigin || !routeDestination) {
        showToast("Debes seleccionar origen y destino.", "error");
        return;
      }
      payload.origin = Number(routeOrigin);
      payload.destination = Number(routeDestination);
    }
    if (!service.id) return;

    updateRoute(service.id, payload, {
      onSuccess: () => {
        onClose();
        showToast(
          routeInvert
            ? "Recorrido invertido correctamente"
            : "Ruta actualizada correctamente",
          "success"
        );
        onSaved?.();
      },
      onError: (resp) =>
        showToast(normalizeUiError(resp?.message ?? resp?.data), "error"),
    });
  };

  return createPortal(
    <div className="sp-quick-passenger-overlay" onClick={onClose}>
      <div
        className="sp-quick-passenger-modal sp-route-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sp-quick-passenger-head">
          <h4>{routeInvert ? "Invertir recorrido" : "Modificar ruta"}</h4>
          <button type="button" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </div>
        <div className="sp-quick-passenger-body">
          <BaseSelect
            label="Origen"
            name="route_origin"
            value={routeOrigin}
            onChange={(e) => {
              setRouteOrigin(e.target.value);
              setRouteInvert(false);
            }}
            placeholder="Seleccionar origen"
            preserveCase
            searchable
            options={locationOptions}
          />
          <RouteStopsEditor
            stops={routeStops}
            onChange={setRouteStops}
            options={locationOptions}
            disabled={Boolean(fetching.getRoute)}
          />
          <div className="sp-route-swap-row">
            <button
              type="button"
              className="sp-route-swap-btn"
              onClick={swapRouteEndpoints}
              title="Intercambiar origen y destino"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M7 16V4M7 4L3 8M7 4l4 4M17 8v12M17 20l4-4M17 20l-4-4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Invertir
            </button>
          </div>
          <BaseSelect
            label="Destino"
            name="route_destination"
            value={routeDestination}
            onChange={(e) => {
              setRouteDestination(e.target.value);
              setRouteInvert(false);
            }}
            placeholder="Seleccionar destino"
            preserveCase
            searchable
            options={locationOptions}
          />
          <label className="sp-route-comment-field">
            Comentario obligatorio
            <textarea
              value={routeComment}
              onChange={(e) => setRouteComment(e.target.value)}
              rows={3}
              placeholder="Describe el motivo del cambio de ruta…"
            />
          </label>
        </div>
        <div className="sp-quick-passenger-actions">
          <button
            type="button"
            className="sp-quick-passenger-btn sp-quick-passenger-btn--ghost"
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="sp-quick-passenger-btn sp-quick-passenger-btn--primary"
            onClick={saveRoute}
            disabled={Boolean(fetching.updateRoute)}
          >
            {fetching.updateRoute ? "Guardando…" : "Confirmar"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ServiceRouteEditModal;
